import React, { useState, useEffect } from 'react';
import lpApi from '../../../helpers/lpApi';

// Big Projects (Large Projects) KPIs on the admin dashboard. Self-contained:
// reads besthand-api's adminDashboardOverview through the /lp-graphql proxy via
// lpApi, so it never touches the web's earnings pipeline. Renders nothing if the
// LP backend isn't reachable / not authorized (keeps the dashboard clean).

const Q_OVERVIEW = `
  query {
    adminDashboardOverview {
      status errorMessage
      adminOverview {
        pendingKyb
        expiringLicenses
        expiringInsurances
        activeContracts
        inCancellationPeriod
        openDisputes
        capturedPayments
        transferredPayouts
        recentCircumventionAttempts7d
      }
    }
  }
`;

const Q_ANALYTICS = `
  query ($periodDays: Int) {
    adminBigProjectsAnalytics(periodDays: $periodDays) {
      status errorMessage
      bigProjectsAnalytics {
        periodDays
        gmv refunds netPayouts platformTake fundsHeld
        requestsTotal requestsDraft requestsPublished
        quotesTotal contractsTotal contractsActive contractsCompleted
        milestonesCompleted
        publishedToQuotedRate quotedToContractedRate
        openDisputes pendingKyb
      }
    }
  }
`;

const usd = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONEY_CARDS = [
  { key: 'gmv', label: 'GMV (captured)', accent: '#0f1214' },
  { key: 'platformTake', label: 'Platform take', accent: '#0a7d3c' },
  { key: 'fundsHeld', label: 'Funds held (escrow)', accent: '#0f1214' },
  { key: 'refunds', label: 'Refunds', accent: '#c2151c' },
  { key: 'netPayouts', label: 'Payouts (net)', accent: '#0f1214' },
];

const CARDS = [
  { key: 'pendingKyb', label: 'Pending KYB', href: '/siteadmin/big-projects/companies' },
  { key: 'activeContracts', label: 'Active projects', href: '/siteadmin/big-projects/contracts' },
  { key: 'openDisputes', label: 'Open disputes', warn: true, href: '/siteadmin/big-projects/disputes' },
  { key: 'expiringLicenses', label: 'Licenses expiring (30d)', warn: true, href: '/siteadmin/big-projects/companies' },
  { key: 'expiringInsurances', label: 'Insurance expiring (30d)', warn: true, href: '/siteadmin/big-projects/companies' },
  { key: 'inCancellationPeriod', label: 'In cancellation window', href: '/siteadmin/big-projects/contracts' },
  { key: 'capturedPayments', label: 'Payments captured', href: '/siteadmin/big-projects/funds' },
  { key: 'transferredPayouts', label: 'Payouts transferred', href: '/siteadmin/big-projects/funds' },
  { key: 'recentCircumventionAttempts7d', label: 'Circumvention flags (7d)', warn: true, href: '/siteadmin/big-projects/circumvention' },
];

function BigProjectsMetrics() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Fetch the overview (gates self-hide) + analytics (additive) together.
        const [ovRes, anRes] = await Promise.all([
          lpApi(Q_OVERVIEW),
          lpApi(Q_ANALYTICS, { periodDays: null }),
        ]);
        const ov = ovRes && ovRes.data && ovRes.data.adminDashboardOverview;
        if (alive && ov && ov.status === 200 && ov.adminOverview) {
          setData(ov.adminOverview);
          const an = anRes && anRes.data && anRes.data.adminBigProjectsAnalytics;
          if (an && an.status === 200 && an.bigProjectsAnalytics) {
            setAnalytics(an.bigProjectsAnalytics);
          }
        } else if (alive) {
          setHidden(true);
        }
      } catch (e) {
        if (alive) setHidden(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (hidden || !data) return null;

  return (
    <div style={{ margin: '24px 0 0' }}>
      <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 14px', color: '#0f1214' }}>
        Big Projects
      </h3>

      {analytics && (
        <div style={{ marginBottom: 22 }}>
          {/* Money row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 14,
            marginBottom: 14,
          }}>
            {MONEY_CARDS.map((c) => (
              <a key={c.key} href="/siteadmin/big-projects/funds" style={{ textDecoration: 'none' }}>
                <div style={{
                  border: '1px solid #e7e9ec',
                  borderRadius: 14,
                  padding: '18px 18px',
                  background: 'linear-gradient(180deg,#ffffff,#fbfcfd)',
                  boxShadow: '0 6px 18px rgba(15,18,20,0.04)',
                }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: c.accent }}>
                    {usd(analytics[c.key])}
                  </div>
                  <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 4, fontWeight: 600 }}>
                    {c.label}
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Funnel row */}
          <div style={{
            border: '1px solid #e7e9ec',
            borderRadius: 14,
            padding: '16px 18px',
            background: '#fff',
            boxShadow: '0 6px 18px rgba(15,18,20,0.03)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12 }}>
              Conversion funnel (all-time)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              {[
                { label: 'Requests', value: analytics.requestsTotal },
                { label: 'Published', value: analytics.requestsPublished, rate: `${analytics.publishedToQuotedRate}% → quoted` },
                { label: 'Quotes', value: analytics.quotesTotal, rate: `${analytics.quotedToContractedRate}% → contracted` },
                { label: 'Contracts', value: analytics.contractsTotal },
                { label: 'Milestones done', value: analytics.milestonesCompleted },
              ].map((step, i, arr) => (
                <React.Fragment key={step.label}>
                  <div style={{ minWidth: 110 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#0f1214' }}>{step.value == null ? 0 : step.value}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{step.label}</div>
                    {step.rate ? (
                      <div style={{ fontSize: 11, color: '#0a7d3c', fontWeight: 700, marginTop: 2 }}>{step.rate}</div>
                    ) : null}
                  </div>
                  {i < arr.length - 1 ? (
                    <div style={{ color: '#c9ced4', fontSize: 20, fontWeight: 700, padding: '0 2px' }}>{'›'}</div>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
        {CARDS.map((c) => {
          const value = data[c.key] == null ? 0 : data[c.key];
          const danger = c.warn && value > 0;
          const card = (
            <div style={{
              border: '1px solid #e7e9ec',
              borderRadius: 14,
              padding: '18px 18px',
              background: '#fff',
              boxShadow: '0 6px 18px rgba(15,18,20,0.03)',
            }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: danger ? '#c2151c' : '#0f1214' }}>{value}</div>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, fontWeight: 600 }}>{c.label}</div>
            </div>
          );
          return c.href ? (
            <a key={c.key} href={c.href} style={{ textDecoration: 'none' }}>{card}</a>
          ) : (
            <div key={c.key}>{card}</div>
          );
        })}
      </div>
    </div>
  );
}

export default BigProjectsMetrics;
